import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { MapPin, Plus, Edit, Trash2, Loader2, Building2 } from "lucide-react";

export default function MobiliarioUbicacionesPage() {
  const [ubicaciones, setUbicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ nombre: "", descripcion: "", edificio: "", piso: "", area: "" });
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mobiliario/ubicaciones`, getAuthHeaders());
      setUbicaciones(res.data || []);
    } catch (err) {
      toast.error("Error al cargar ubicaciones");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setFormData({ nombre: "", descripcion: "", edificio: "", piso: "", area: "" }); setShowDialog(true); };
  const openEdit = (ub) => { setEditing(ub); setFormData({ nombre: ub.nombre, descripcion: ub.descripcion || "", edificio: ub.edificio || "", piso: ub.piso || "", area: ub.area || "" }); setShowDialog(true); };

  const handleSubmit = async () => {
    if (!formData.nombre) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/mobiliario/ubicaciones/${editing.id}`, formData, getAuthHeaders());
        toast.success("Ubicación actualizada");
      } else {
        await axios.post(`${API}/mobiliario/ubicaciones`, formData, getAuthHeaders());
        toast.success("Ubicación creada");
      }
      setShowDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar ubicación?")) return;
    try {
      await axios.delete(`${API}/mobiliario/ubicaciones/${id}`, getAuthHeaders());
      toast.success("Eliminada"); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900"><Sidebar /><main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></main></div>;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="flex justify-between mb-6">
          <div><h1 className="text-3xl font-bold dark:text-white">Ubicaciones de Mobiliario</h1><p className="text-slate-500">{ubicaciones.length} ubicaciones</p></div>
          <Button onClick={openNew} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"><Plus className="w-4 h-4 mr-2" />Nueva</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ubicaciones.map(ub => (
            <Card key={ub.id} className="hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-[#0C314C]" />
                  <span className="font-medium dark:text-white">{ub.nombre}</span>
                </div>
                {ub.edificio && <p className="text-sm text-slate-500"><Building2 className="w-3 h-3 inline mr-1" />{ub.edificio}{ub.piso && ` - Piso ${ub.piso}`}{ub.area && ` - ${ub.area}`}</p>}
                {ub.descripcion && <p className="text-sm text-slate-500 mt-1">{ub.descripcion}</p>}
                <div className="flex justify-between items-center mt-3">
                  <span className="text-sm text-slate-500">{ub.total_activos || 0} activos</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(ub)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(ub.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle><MapPin className="w-5 h-5 inline mr-2" />{editing ? "Editar" : "Nueva"} Ubicación</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Nombre *</Label><Input value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} placeholder="Ej: Oficina Principal" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Edificio</Label><Input value={formData.edificio} onChange={e => setFormData({...formData, edificio: e.target.value})} placeholder="Torre A" /></div>
                <div><Label>Piso</Label><Input value={formData.piso} onChange={e => setFormData({...formData, piso: e.target.value})} placeholder="3" /></div>
                <div><Label>Área</Label><Input value={formData.area} onChange={e => setFormData({...formData, area: e.target.value})} placeholder="Contabilidad" /></div>
              </div>
              <div><Label>Descripción</Label><Input value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#0C314C] text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
